import React, { useState, useEffect } from 'react';
import { Plus, Upload, Receipt, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ExpenseCard, type Expense } from './ExpenseCard';
import { AddExpenseModal } from './AddExpenseModal';
import { EditExpenseModal } from './EditExpenseModal';
import { Expenses } from '@/integrations/api';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/components/ui/use-toast';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

export function EmployeeDashboard() {
  const { user, userRole } = useAuth();
  const [unpaidExpenses, setUnpaidExpenses] = useState<Expense[]>([]);
  const [paidExpenses, setPaidExpenses] = useState<Expense[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [loading, setLoading] = useState(true);
  const paidCount = paidExpenses.length;

  const transformExpenses = (expenses: any[]): Expense[] => {
    return expenses.map(expense => ({
      id: expense.id,
      amount: Number(expense.amount),
      description: expense.description || 'No description',
      expenseType: expense.expense_type || undefined,
      paymentMethod: expense.payment_method || undefined,
      date: expense.receipt_date || expense.submitted_at.split('T')[0],
      paidBy: 'You',
      status: expense.status as 'pending' | 'recorded' | 'paid' | 'rejected',
      receiptUrl: expense.receipt_url || undefined,
      currency: expense.currency || 'DKK',
      participants: expense.participants || undefined,
      rejectionReason: expense.rejection_reason || undefined
    }));
  };

  const fetchExpenses = async () => {
    if (!user) return;

    try {
      // Fetch all of the employee's own expenses once, then partition in JS.
      const data = await Expenses.listMine();
      const all = transformExpenses(data || []);
      setUnpaidExpenses(all.filter(e => e.status !== 'paid'));
      setPaidExpenses(all.filter(e => e.status === 'paid'));
    } catch (error) {
      console.error('Error:', error);
      toast({ title: "Error", description: "An unexpected error occurred", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenses();
  }, [user, userRole]);

  const handleAddExpense = () => {
    setShowAddModal(false);
    fetchExpenses(); // Refresh the list
  };

  const handleEditSubmit = () => {
    setShowEditModal(false);
    setEditingExpense(null);
    fetchExpenses(); // Refresh the list
  };

  const handleEditExpense = (expense: Expense) => {
    setEditingExpense(expense);
    setShowEditModal(true);
  };

  const handleDeleteExpense = async (expenseId: string) => {
    try {
      // Backend enforces the pending/rejected-only deletion rule.
      await Expenses.remove(expenseId);

      toast({
        title: "Success",
        description: "Expense deleted successfully"
      });

      fetchExpenses(); // Refresh the list
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive"
      });
    }
  };

  const nonRejected = unpaidExpenses.filter(e => e.status !== 'rejected');
  const pendingByCurrency: Record<string, number> = {};
  const recordedByCurrency: Record<string, number> = {};
  nonRejected.forEach(e => {
    const c = e.currency || 'DKK';
    if (e.status === 'pending') {
      pendingByCurrency[c] = (pendingByCurrency[c] || 0) + e.amount;
    } else if (e.status === 'recorded') {
      recordedByCurrency[c] = (recordedByCurrency[c] || 0) + e.amount;
    }
  });

  const renderCurrencyTotals = (totals: Record<string, number>) => {
    const entries = Object.entries(totals);
    if (entries.length === 0) return <div className="text-2xl md:text-3xl font-bold">0.00</div>;
    return entries.map(([currency, amount]) => (
      <div key={currency} className="text-2xl md:text-3xl font-bold">{amount.toFixed(2)} {currency}</div>
    ));
  };

  return (
    <div className="min-h-screen bg-gradient-surface p-2 md:p-4 space-y-3 md:space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start md:items-center gap-2">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl md:text-2xl font-bold text-foreground">My Expenses</h1>
          <p className="text-sm md:text-base text-muted-foreground hidden md:block">Track and manage your receipts</p>
        </div>
        <Button onClick={() => setShowAddModal(true)} className="gap-2 flex-shrink-0" size="sm">
          <Plus className="h-4 w-4" />
          <span className="hidden sm:inline">Add Expense</span>
          <span className="sm:hidden">Add</span>
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
        <Card className="bg-gradient-primary text-primary-foreground shadow-medium">
          <CardHeader className="pb-2 md:pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Receipt className="h-4 w-4" />
              Pending
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {renderCurrencyTotals(pendingByCurrency)}
            <p className="text-primary-foreground/80 text-xs md:text-sm">
              {nonRejected.filter(e => e.status === 'pending').length} not yet recorded
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-accent text-accent-foreground shadow-medium">
          <CardHeader className="pb-2 md:pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="h-4 w-4" />
              Recorded
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {renderCurrencyTotals(recordedByCurrency)}
            <p className="text-accent-foreground/80 text-xs md:text-sm">
              {nonRejected.filter(e => e.status === 'recorded').length} awaiting payment
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions - More compact on mobile */}
      <Card className="hover:shadow-medium transition-all duration-200 cursor-pointer md:block hidden"
            onClick={() => setShowAddModal(true)}>
        <CardContent className="p-4 md:p-6 text-center">
          <Upload className="h-6 w-6 md:h-8 md:w-8 text-primary mx-auto mb-2" />
          <h3 className="font-semibold text-sm md:text-base">Upload Receipt</h3>
          <p className="text-muted-foreground text-xs md:text-sm">Scan or upload a new receipt</p>
        </CardContent>
      </Card>

      {/* Expenses Sections */}
      <div className="space-y-6">
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">
            Loading expenses...
          </div>
        ) : (
          <>
            {/* Unpaid Expenses Section */}
            <div>
              <h2 className="text-base md:text-lg font-semibold text-foreground mb-2 md:mb-4">
                Unpaid Expenses
              </h2>
              {unpaidExpenses.length === 0 ? (
                <div className="text-center py-8">
                  <Receipt className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">No unpaid expenses</h3>
                  <p className="text-muted-foreground mb-4">
                    {paidExpenses.length > 0 
                      ? "All your expenses have been paid out!" 
                      : "Start by adding your first expense"
                    }
                  </p>
                  <Button onClick={() => setShowAddModal(true)} className="gap-2">
                    <Plus className="h-4 w-4" />
                    Add Expense
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-2 md:gap-4">
                  {unpaidExpenses.map((expense) => (
                    <ExpenseCard
                      key={expense.id}
                      expense={expense}
                      onEdit={handleEditExpense}
                      onDelete={handleDeleteExpense}
                      showActions={true}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Paid Expenses Accordion */}
            {paidCount > 0 && (
              <Accordion type="single" collapsible>
                <AccordionItem value="paid">
                  <AccordionTrigger className="text-base md:text-lg font-semibold text-foreground">
                    Paid Expenses ({paidCount})
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-1 gap-2 md:gap-4 pt-2">
                      {paidExpenses.map((expense) => (
                        <ExpenseCard
                          key={expense.id}
                          expense={expense}
                          showActions={false}
                        />
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            )}
          </>
        )}
      </div>

      <AddExpenseModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddExpense}
      />

      <EditExpenseModal
        open={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingExpense(null);
        }}
        onSubmit={handleEditSubmit}
        expense={editingExpense}
      />
    </div>
  );
}